import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Spinner } from "theme-ui";
import { useNavigate } from "react-router-dom";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import Subtext from "components/typography/Subtext";
import BasicButton from "components/forms/BasicButton";
import { mnt, mntCard, mntType } from "theme/maintenanceTokens";

import { getPortalLocations, getPortalLocationMapPoints, PortalLocation } from "api/LocationClientProfiles";
import ImportSitesModal from "containers/Clients/components/ImportSitesModal";
import RequestWorkModal from "containers/Maintenance/components/RequestWorkModal";
import ClientLocationsMap, {
  ClientMapPoint,
} from "./components/ClientLocationsMap";

/**
 * N1 Release 1 - CLIENT PORTAL Locations page.
 *
 * The client-facing mirror of the internal locations surfaces, but
 * READ-ONLY and scoped entirely to the signed-in client's own sites (the
 * backend `/portal/locations` endpoint enforces role + the
 * portalLocationsEnabled capability + parent-aware ownership; this page
 * trusts that scoping and just renders what comes back).
 *
 * Composition: the map leads (it is the most legible view of "your
 * sites"), with a simple list beneath it. No internal work-order state,
 * no pricing - just the client's locations. The heavier cross-navigation
 * redesign between this page and the Mulch Calculator is a Release 2
 * task.
 */
export default function PortalLocations() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<PortalLocation[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  /**
   * PAGING (Session 80). Production has a client with 1,811 sites. This
   * page used to fetch and render every one of them.
   *
   * TWENTY, not fifty. Page size here is about SCROLLING, not round
   * trips: the map already takes most of the first screen, so fifty rows
   * beneath it is several screens of scrolling to reach a pager. Twenty
   * lands just under two screens. It makes 1,811 sites 91 pages, which
   * sounds worse and is not — nobody pages to 91. Search is how you find
   * a site; the pager is for looking around near where you are.
   *
   * The MAP is deliberately NOT paged — it reads its own coordinates-only
   * endpoint covering every site. A map showing 20 of 1,811 answers
   * "where is my portfolio" with a lie, and the pins are cheap precisely
   * because they carry no plan or work-order lookups.
   */
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [mapPoints, setMapPoints] = useState<ClientMapPoint[]>([]);
  // The self-service-granting plan for the ORG, from the server. A ref
  // rather than state: it only ever goes from null to a value, and
  // nothing needs to re-render the moment it is found.
  const selfServicePlanRef = useRef<any>(null);
  // Session 62: clients can bulk-import their own sites via the shared
  // CSV importer (no plan -> backend ties each site to this client's own
  // org). This page is only reachable when portalLocationsEnabled is on
  // (nav + backend gate), so no extra capability check is needed here;
  // the backend re-checks it on the import call regardless.
  const [showSiteImport, setShowSiteImport] = useState(false);

  /**
   * KNOWN_ISSUES item 18 — "Request work" from a location row.
   *
   * The endpoint is plan-scoped, so a request needs a plan for CONTEXT
   * even when the site itself is not on one. We use the client's first
   * self-service-enabled plan. That is not a fudge: the plan is what
   * grants the client the right to raise work at all, and the SERVER
   * decides where the work lands from the site's roster membership, not
   * from the plan in the route. A site on the plan lands on it; a site
   * off it is quoted separately. Both are correct through the same call.
   *
   * With no enabled plan there is no button anywhere on this page —
   * self-service is a per-plan grant and a client without one has not
   * been given it.
   */
  const [requestSite, setRequestSite] = useState<any>(null);
  // Which mode the modal opens in. A site on a plan gets both doors:
  // "Request work" when they cannot say how much, "Create work order"
  // when they can and want it priced. Same distinction the plan page
  // makes; there is no reason the site list should offer only half of it.
  const [requestMode, setRequestMode] = useState<"request" | "order">("request");
  // Server-side search. It used to filter the browser's copy of every
  // row, which only worked while the browser HAD every row. On a paged
  // list that would quietly search one page of 1,811 sites and look like
  // an answer. `searchInput` is what the user is typing; `search` is the
  // debounced value the request uses.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Open a location detail page (the same page the internal team uses;
  // the backend scopes a client to their own org's locations). Mirrors
  // the calculator's recent-address click. Falls back to the profile id
  // only if a locationId is somehow absent (shouldn't happen).
  const openLocation = (locationId: string | null) => {
    if (locationId) navigate(`/locations/${locationId}`);
  };

  const loadLocations = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    getPortalLocations({ page, pageSize: PAGE_SIZE, search })
      .then((res) => {
        if (cancelled) return;
        setLocations(res.items);
        setTotal(res.total);
        // The org's grant comes from the server, not from scanning the
        // page — the granting plan may sit on any page, and the buttons
        // must not appear and vanish as someone moves between them.
        if (!selfServicePlanRef.current && res.orgSelfServicePlan) {
          selfServicePlanRef.current = res.orgSelfServicePlan;
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLocations([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, search]);

  // Debounce the typing so a search does not fire a request per keystroke
  // against a 1,811-row table, and reset to page 1: staying on page 7 of
  // a new search shows an empty list and reads as "no results".
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Locations";
    const cleanup = loadLocations();
    return cleanup;
  }, [loadLocations]);

  // The map, loaded ONCE and independently of the page. Its own endpoint
  // returns coordinates for every site the client owns.
  useEffect(() => {
    let cancelled = false;
    getPortalLocationMapPoints()
      .then((pts) => {
        if (cancelled) return;
        setMapPoints(
          pts.map((p) => ({
            locationId: p.locationId,
            profileId: p.profileId,
            name: p.name,
            address: p.address,
            latitude: p.latitude,
            longitude: p.longitude,
          })),
        );
      })
      .catch(() => {
        // No map is survivable; a broken page is not.
        if (!cancelled) setMapPoints([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const formatAddress = (address: any): string => {
    if (!address || typeof address !== "object") return "";
    return [address.line1, address.city, address.state, address.postalCode]
      .filter(Boolean)
      .join(", ");
  };

  if (loading && locations === null) {
    return (
      <>
        <Header></Header>
        <Page title="Locations">
          <Box sx={{ textAlign: "center", paddingY: "40px" }}>
            <Spinner />
          </Box>
        </Page>
      </>
    );
  }

  const rows = locations || [];
  const searchQuery = search;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // The plan that grants self-service, if any. See the note on
  // `requestSite` above for why one plan can serve rows that are not on
  // it. Answered by the server, so it does not depend on which page
  // happens to be loaded.
  const selfServicePlan = selfServicePlanRef.current;

  return (
    <>
      <Header></Header>
      <Page title="Locations" $noPadding={true}>
        <Box
          sx={{
            maxWidth: "1200px",
            marginX: "auto",
            width: "100%",
            // `$noPadding` on Page zeroes the BOTTOM padding, so without
            // this the last row sits flush against the viewport edge and
            // reads as cut off. Every other maintenance surface that
            // passes $noPadding adds its own bottom padding for the same
            // reason; this page never did.
            paddingBottom: "40px",
          }}
        >
          <Box sx={{ marginTop: "16px", marginBottom: "18px" }}>
            <H5
              fontFamily="Poppins"
              fontWeight={600}
              margin={0}
              padding={0}
              fontSize={"20px"}
            >
              Your locations
            </H5>
            <Flex
              sx={{
                justifyContent: "space-between",
                alignItems: "flex-end",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <Subtext
                fontSize={"13px"}
                fontFamily={"Poppins"}
                marginTop={"6px"}
                marginBottom={0}
              >
                {total === 0
                  ? searchQuery
                    ? `No match for \u201C${searchQuery}\u201D`
                    : "No locations to show yet."
                  : searchQuery
                    ? `${total} match${total === 1 ? "" : "es"} for \u201C${searchQuery}\u201D`
                    : `${total} location${total === 1 ? "" : "s"}`}
                {pageCount > 1 &&
                  ` \u00b7 page ${page} of ${pageCount}`}
              </Subtext>
              <Flex sx={{ alignItems: "center", gap: "10px" }}>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e: any) => setSearchInput(e.target.value)}
                  placeholder={"Search sites\u2026"}
                  style={{
                    fontFamily: "Poppins",
                    fontSize: "12.5px",
                    padding: "7px 11px",
                    borderRadius: "4px",
                    border: `1px solid ${mnt.divider}`,
                    color: mnt.ink,
                    minWidth: "220px",
                  }}
                />
                <BasicButton
                  type="button"
                  outline
                  scheme="secondary"
                  onClick={() => setShowSiteImport(true)}
                  keepText
                >
                  Import sites (CSV)
                </BasicButton>
              </Flex>
            </Flex>
          </Box>

          {rows.length === 0 ? (
            <Box sx={{ ...mntCard, padding: "24px", textAlign: "center" }}>
              <P fontSize={"14px"} color={mnt.textMuted} margin={0}>
                {searchQuery
                  ? "No sites match that search."
                  : "There are no locations associated with your account yet."}
              </P>
            </Box>
          ) : (
            <>
              {/* Map leads - the most legible view of a client's sites. */}
              <Box sx={{ marginBottom: "20px" }}>
                <ClientLocationsMap
                  points={mapPoints}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onOpenLocation={openLocation}
                />
              </Box>

              {/* Simple list beneath the map. Clicking a row OPENS the
                  location (same detail page the team uses, client-scoped
                  server-side). Single-click on the row navigates; the map
                  pin popover has its own Open button. */}
              <Box sx={{ ...mntCard, padding: "6px 0" }}>
                {rows.map((l) => {
                  const id = l.locationId ?? l.profileId;
                  const isSelected = selectedId === id;
                  const addr = formatAddress(l.address);
                  const onPlan = !!l.plan;
                  const openWo = (l as any).openWorkOrder ?? null;
                  // Session 80 follow-up — acknowledge recent cancelled
                  // work. Only ever set when nothing is open, so it can
                  // never compete with a live job.
                  const cancelledWo = (l as any).cancelledWorkOrder ?? null;
                  return (
                    <Box
                      key={l.profileId}
                      onClick={() => openLocation(l.locationId)}
                      onMouseEnter={() => setSelectedId(id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        width: "100%",
                        textAlign: "left",
                        background: isSelected ? mnt.bgSubtle : "transparent",
                        borderBottom: `1px solid ${mnt.divider}`,
                        padding: "12px 16px",
                        cursor: "pointer",
                        "&:last-of-type": { borderBottom: "none" },
                        "&:hover": { background: mnt.bgSubtle },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            ...mntType.labelSm,
                            color: mnt.ink,
                            fontWeight: 600,
                            fontSize: "14px",
                          }}
                        >
                          {l.name || "Location"}
                        </Box>
                        {addr && (
                          <Box
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "12px",
                              color: mnt.textMuted,
                              marginTop: "2px",
                            }}
                          >
                            {addr}
                          </Box>
                        )}
                      </Box>

                      <Flex sx={{ alignItems: "center", gap: "10px", flexShrink: 0 }}>
                        {/* THE PLAN BADGE, and only when there IS one.

                            It used to render "Not on a plan" on every
                            other row. On a PLAN's locations table that
                            would be information — these sites are
                            covered, those are not. On the client's own
                            locations list it is not: most of these
                            clients have no maintenance plan at all, so
                            the pill labelled every site with the absence
                            of a thing they have never had. That reads as
                            a defect or a sales prompt, and it is neither.

                            A plan name still shows, because "this site
                            is covered" is the fact that stops a client
                            being surprised by a separate invoice. Its
                            absence says nothing and so says nothing. */}
                        {onPlan && (
                          <Box
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "11px",
                              fontWeight: 500,
                              padding: "3px 9px",
                              borderRadius: "10px",
                              whiteSpace: "nowrap",
                              background: mnt.greenPill,
                              color: mnt.greenFg,
                            }}
                          >
                            {`${l.plan?.name || "Maintenance"} plan`}
                          </Box>
                        )}

                        {/* BOTH doors on every row the grant covers, on
                            or off plan. The distinction that matters is
                            "can you tell us how much", not "is this site
                            enrolled" — and the modal already handles the
                            off-plan case, saying plainly that the work
                            will be quoted and invoiced on its own.
                            Showing only Request work on an off-plan site
                            hid the faster path for no reason.

                            A site with an OPEN work order gets neither:
                            raising a second one for the same site is
                            almost never what they meant, and the row
                            cannot warn them about a duplicate it cannot
                            see. It links to the live one instead — which
                            is also what someone checking on progress is
                            actually after.
                            stopPropagation because the row navigates. */}
                        {/* Session 80 — a site whose open work order has
                            an outstanding cancellation request says so.
                            Without it the row reads as ordinary live
                            work and the client cannot tell, from the
                            page they use to check on things, that we
                            have their request at all. */}
                        {openWo?.cancellationRequestedAt && (
                          <Box
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "11px",
                              fontWeight: 500,
                              padding: "3px 9px",
                              borderRadius: "10px",
                              whiteSpace: "nowrap",
                              background: mnt.amberPill,
                              color: mnt.amberDeep,
                            }}
                          >
                            Cancellation requested
                          </Box>
                        )}

                        {/* A work order cancelled in the last week, where
                            nothing is open. It is an ACKNOWLEDGMENT, not a
                            status: muted, clickable to the record, and it
                            does NOT suppress the buttons — the site is
                            available for new work again, which is the
                            thing the client most needs to be able to act
                            on. It ages out on its own. */}
                        {!openWo && cancelledWo && (
                          <button
                            type="button"
                            onClick={(e: any) => {
                              e.stopPropagation();
                              navigate(`/maintenance/work-orders/${cancelledWo.id}`);
                            }}
                            style={{
                              fontFamily: "Poppins",
                              fontSize: "11px",
                              fontWeight: 500,
                              padding: "3px 9px",
                              borderRadius: "10px",
                              whiteSpace: "nowrap",
                              background: mnt.greyPill,
                              border: "none",
                              color: mnt.grey,
                              cursor: "pointer",
                            }}
                          >
                            {cancelledWo.externalId
                              ? `${cancelledWo.externalId} cancelled`
                              : "Work order cancelled"}
                          </button>
                        )}

                        {openWo ? (
                          <button
                            type="button"
                            onClick={(e: any) => {
                              e.stopPropagation();
                              navigate(`/maintenance/work-orders/${openWo.id}`);
                            }}
                            style={{
                              fontFamily: "Poppins",
                              fontSize: "11.5px",
                              fontWeight: 500,
                              padding: "5px 11px",
                              borderRadius: "4px",
                              cursor: "pointer",
                              background: mnt.ink,
                              border: `1px solid ${mnt.ink}`,
                              color: "#fff",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {openWo.externalId
                              ? `View ${openWo.externalId}`
                              : "View work order"}
                          </button>
                        ) : (
                          <>
                        {selfServicePlan && (
                          <button
                            type="button"
                            onClick={(e: any) => {
                              e.stopPropagation();
                              setRequestMode("request");
                              setRequestSite({
                                locationId: l.locationId,
                                name: l.name,
                                city: l.address?.city ?? null,
                                onPlan,
                                planName: l.plan?.name ?? null,
                              });
                            }}
                            style={{
                              fontFamily: "Poppins",
                              fontSize: "11.5px",
                              fontWeight: 500,
                              padding: "5px 11px",
                              borderRadius: "4px",
                              cursor: "pointer",
                              background: mnt.cardBg,
                              border: `1px solid ${mnt.divider}`,
                              color: mnt.ink,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Request work
                          </button>
                        )}

                        {selfServicePlan && (
                          <button
                            type="button"
                            onClick={(e: any) => {
                              e.stopPropagation();
                              setRequestMode("order");
                              setRequestSite({
                                locationId: l.locationId,
                                name: l.name,
                                city: l.address?.city ?? null,
                                onPlan,
                                planName: l.plan?.name ?? null,
                              });
                            }}
                            style={{
                              fontFamily: "Poppins",
                              fontSize: "11.5px",
                              fontWeight: 500,
                              padding: "5px 11px",
                              borderRadius: "4px",
                              cursor: "pointer",
                              background: mnt.ink,
                              border: `1px solid ${mnt.ink}`,
                              color: "#fff",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Create work order
                          </button>
                        )}
                          </>
                        )}

                        <Box
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "18px",
                            color: mnt.textFaint,
                          }}
                          aria-hidden="true"
                        >
                          {"\u203A"}
                        </Box>
                      </Flex>
                    </Box>
                  );
                })}
              </Box>

              {/* PAGER. Only when there is more than one page — a client
                  with four sites should never see paging controls. Prev
                  and Next only: a client is looking for a site by name,
                  which the search does, not navigating to page 19 of 37.
                  1,811 sites is 37 pages and a numbered strip would be
                  its own scrolling problem. */}
              {pageCount > 1 && (
                <Flex
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginTop: "14px",
                    flexWrap: "wrap",
                  }}
                >
                  <Subtext
                    fontSize={"12px"}
                    fontFamily={"Poppins"}
                    margin={0}
                    color={mnt.textMuted}
                  >
                    {`Showing ${(page - 1) * PAGE_SIZE + 1}\u2013${Math.min(
                      page * PAGE_SIZE,
                      total,
                    )} of ${total}`}
                  </Subtext>
                  <Flex sx={{ alignItems: "center", gap: "8px" }}>
                    <button
                      type="button"
                      disabled={page <= 1 || loading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        fontWeight: 500,
                        padding: "6px 12px",
                        borderRadius: "4px",
                        background: mnt.cardBg,
                        border: `1px solid ${mnt.divider}`,
                        color: page <= 1 ? mnt.textFaint : mnt.ink,
                        cursor: page <= 1 ? "default" : "pointer",
                      }}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      disabled={page >= pageCount || loading}
                      onClick={() =>
                        setPage((p) => Math.min(pageCount, p + 1))
                      }
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        fontWeight: 500,
                        padding: "6px 12px",
                        borderRadius: "4px",
                        background: mnt.cardBg,
                        border: `1px solid ${mnt.divider}`,
                        color: page >= pageCount ? mnt.textFaint : mnt.ink,
                        cursor: page >= pageCount ? "default" : "pointer",
                      }}
                    >
                      Next
                    </button>
                  </Flex>
                </Flex>
              )}
            </>
          )}
        </Box>
      </Page>

      {showSiteImport && (
        <ImportSitesModal
          onClose={() => setShowSiteImport(false)}
          onImported={loadLocations}
        />
      )}

      {requestSite && selfServicePlan && (
        <RequestWorkModal
          planId={selfServicePlan.id}
          planName={selfServicePlan.name}
          mode={requestMode}
          presetSite={requestSite}
          onClose={() => setRequestSite(null)}
          onCreated={loadLocations}
        />
      )}
    </>
  );
}
